import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const LogisticsPage = () => {
  const capabilities = [
    {
      title: 'Fleet & Dispatch Optimization',
      description: 'Route planning, fleet utilization analysis, and dispatch system improvements for maximum efficiency.',
      icon: 'ri-truck-line',
    },
    {
      title: 'FMCSA & DOT Compliance Advisory',
      description: 'Regulatory compliance support, safety program development, and audit preparation.',
      icon: 'ri-file-shield-line',
    },
    {
      title: 'Cost & Route Optimization',
      description: 'Fuel cost reduction, route optimization, and operational expense management strategies.',
      icon: 'ri-route-line',
    },
    {
      title: 'Warehouse & Last-Mile Strategy',
      description: 'Warehouse operations improvement and last-mile delivery optimization solutions.',
      icon: 'ri-building-4-line',
    },
    {
      title: 'Transportation Technology Integration',
      description: 'TMS implementation, GPS tracking, and logistics technology adoption support.',
      icon: 'ri-radar-line',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                  <span className="text-xs uppercase tracking-wider text-copper">Logistics Advisory</span>
                </div>
                <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                  Logistics &{' '}
                  <span className="bg-copper-gradient bg-clip-text text-transparent">
                    Transportation Advisory
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed mb-10">
                  Expert guidance for transportation companies, fleet operators, and logistics networks requiring compliance support, operational efficiency, and technology integration.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/cb3a70a03b7066ee58c5edb873b55502.jpg"
                  alt="Logistics Advisory"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Logistics Capabilities</h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Comprehensive transportation and logistics consulting services designed to improve efficiency, ensure compliance, and reduce operational costs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-charcoal border border-charcoal-light rounded-2xl p-8 hover:border-copper transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-4xl text-copper`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{capability.title}</h3>
                  <p className="text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=transportation%20logistics%20professionals%20analyzing%20route%20optimization%20data%20on%20digital%20screens%20in%20modern%20dispatch%20center%2C%20fleet%20management%20team%20working%20with%20technology%20systems%20in%20professional%20operations%20environment&width=700&height=400&seq=logistics-approach-002&orientation=landscape"
                  alt="Our Approach"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Our Approach</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  We combine industry expertise with data-driven analysis to deliver practical solutions that improve fleet performance, ensure regulatory compliance, and reduce operational costs. Our methodology focuses on sustainable improvements and measurable results.
                </p>
                <ul className="space-y-4">
                  {[
                    'Comprehensive operational assessment and gap analysis',
                    'Regulatory compliance review and remediation planning',
                    'Fleet utilization and route optimization analysis',
                    'Technology evaluation and implementation support',
                    'Performance monitoring and continuous improvement',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-copper text-xl mt-1"></i>
                      <span className="text-base text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Optimize Your Transportation Operations
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Partner with MACGOC to improve fleet efficiency, ensure compliance, and reduce costs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LogisticsPage;